import React, { useState } from 'react'
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Code as CodeIcon } from '@material-ui/icons'
import { Loader as LoaderIcon } from 'react-feather'

import { AgeGateCompilationCard, CompilationDetailsCard } from './components'
import BannerHeadCard from '../BannerHeadCard'
import BannerCode from '../BannerInstallationCard/components/BannerCode'

import useSnackbar from 'hooks/useSnackbar'
import useCookieBanner from 'hooks/useCookieBanner'
import styles from './styles'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'

const BannerCompilationCard = () => {
  const {
    ageGate,
    banner,
    isCompiling,
    setIsCompiling,
    isScanning,
    refreshBanner,
  } = useCookieBanner()
  const useStyles = styles(isCompiling)
  const classes = useStyles()
  const snackbar = useSnackbar()

  const build = banner?.build
  const bannerToken = banner?.token
  const ageGateEnabled = ageGate?.ageGateEnabled

  const buildCompleted =
    build?.status === constants.cookies.build.COMPLETED_BUILD_STATUS_ID
  const buildInitiated =
    isCompiling ||
    build?.status === constants.cookies.build.INITIATED_BUILD_STATUS_ID

  const disableCompile = isScanning || buildInitiated
  const canSeeCode = !buildInitiated && buildCompleted

  const [openBannerCode, setOpenBannerCode] = useState(buildCompleted || false)

  const handleCompile = async () => {
    setIsCompiling(true)
    try {
      await service.cookies.banner.compile({ bannerId: banner.id })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      refreshBanner()
    }
  }

  const handleOpenHtmlDialog = () =>
    window.open(constants.cookies.banner.LINK_DOCUMENTATION_HTML)
  const handleOpenTagManagerDialog = () =>
    window.open(constants.cookies.banner.LINK_DOCUMENTATION_TAG_MANAGER)

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
      <Paper
        variant="outlined"
        id={constants.cookies.banner.COOKIES_BANNER_SETTING_DRIVER_STEP_5}
      >
        <BannerHeadCard name="Código de Instalação" />
        <Divider />
        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
        >
          <CompilationDetailsCard build={build} />
          <Box
            bgcolor={theme.palette.custom.light}
            borderRadius={1}
            display="flex"
            flexDirection="column"
            gridGap={theme.spacing(3)}
            p={2}
            textAlign="justify"
          >
            <Typography color="primary" textAlign="justify">
              Para instalar o banner de cookies, escolha o modo de instalação
              apropriado para o seu site: <strong>HTML</strong> ou{' '}
              <strong>Google Tag Manager</strong>. Em seguida, copie o código
              HTML abaixo e siga as instruções de instalação conforme o modo
              selecionado.
            </Typography>
          </Box>
          <Alert variant="outlined" severity="info">
            <strong>Recomendamos a instalação via Google Tag Manager.</strong>{' '}
            Essa modalidade garante maior rastreabilidade, conformidade com a
            LGPD e bloqueia cookies indesejados com mais precisão — sem
            necessidade de alterações no código-fonte do site.
          </Alert>
          <Alert variant="outlined" severity="warning">
            <strong>Instalação via HTML:</strong> A instalação via HTML é mais
            simples, mas bloqueia os cookies somente após o carregamento da
            página, podendo comprometer a rastreabilidade. É necessário ter
            acesso ao código-fonte do site.
          </Alert>
          <Box className={classes.buttonContainer}>
            <Box className={classes.buttonBox}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenHtmlDialog}
              >
                Como instalar com HTML
              </Button>
            </Box>
            <Box className={classes.buttonBox}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenTagManagerDialog}
              >
                Como instalar com Tag Manager
              </Button>
            </Box>
            <Box className={classes.buttonBox}>
              <Tooltip
                title={
                  (!canSeeCode &&
                    'O código do banner só pode ser mostrado após a compilação ter sido realizada com sucesso.') ||
                  ''
                }
              >
                <span className={classes.tooltipSpan}>
                  <Button
                    startIcon={<CodeIcon size={18} />}
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => setOpenBannerCode(!openBannerCode)}
                    disabled={!canSeeCode}
                  >
                    Ver código
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Box className={classes.buttonBox}>
              <Tooltip
                title={
                  isScanning
                    ? 'A compilação só pode ser feita depois que o scanner for completo.'
                    : buildInitiated
                    ? 'Aguarde a geração do código finalizar.'
                    : ''
                }
              >
                <span className={classes.tooltipSpan}>
                  <Button
                    startIcon={
                      <LoaderIcon size={18} className={classes.iconAnimation} />
                    }
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={disableCompile}
                    onClick={handleCompile}
                  >
                    {isCompiling ? 'Gerando...' : 'Gerar'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Collapse in={openBannerCode}>
            <BannerCode bannerToken={bannerToken} />
          </Collapse>
        </Box>
      </Paper>

      {ageGateEnabled && <AgeGateCompilationCard />}
    </Box>
  )
}

export default BannerCompilationCard
